/**
 * @file Gallery.js
 * @author Stanley Chen
 * @description Component that displays a responsive masonry layout of restaurant images
 * with a modal view for enlarged images and navigation controls
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import './Gallery.css';

const galleryImages = [
  { src: '/images/gallery/gallery-1.jpg', alt: 'Wall Decor at Kiku' },
  { src: '/images/gallery/gallery-2.jpg', alt: 'Kiku Interior Dining Area' },
  { src: '/images/gallery/gallery-3.jpg', alt: 'Experience Hibachi Fire' },
  { src: '/images/gallery/gallery-5.jpg', alt: 'Sushi Bar seating' },
  { src: '/images/gallery/gallery-6.jpg', alt: 'Hibachi Combo with steak, shrimp and lobster tail' },
  { src: '/images/gallery/gallery-7.jpg', alt: 'Restaurant interior' },
  { src: '/images/gallery/gallery-8.jpg', alt: 'Overhead view of Kiku dishes' },
  { src: '/images/gallery/gallery-9.jpg', alt: 'Hurricane Prawns (Appetizer)' },
  { src: '/images/gallery/gallery-10.jpg', alt: 'Kiku Interior Dining Area' },
  { src: '/images/gallery/gallery-11.jpg', alt: 'Kiku Interior Hibachi Area' },
  { src: '/images/gallery/gallery-12.jpg', alt: 'Overhead view of Kiku dishes' },
  { src: '/images/gallery/gallery-13.jpg', alt: 'Kiku Interior Decor' },
  { src: '/images/gallery/gallery-14.jpg', alt: 'Kiku Ceiling Decor' },
  { src: '/images/gallery/gallery-15.jpg', alt: 'Kiku Interior Decor' },
  { src: '/images/gallery/gallery-16.jpg', alt: 'Sashimi Dinner Platter' },
  { src: '/images/gallery/gallery-17.jpg', alt: 'Drink' },
  { src: '/images/gallery/gallery-18.jpg', alt: 'Drink' },
  { src: '/images/gallery/gallery-19.jpg', alt: 'Kiku Bar area' },
  { src: '/images/gallery/gallery-20.jpg', alt: 'Sushi roll' },
  { src: '/images/gallery/gallery-21.jpg', alt: 'Chicken Hibachi noodles' },
  { src: '/images/gallery/gallery-22.jpg', alt: 'Kiku Sushi Bar Decor' },
  { src: '/images/gallery/gallery-23.jpg', alt: 'Sashimi Platter' },
  { src: '/images/gallery/gallery-24.jpg', alt: 'Shrimp Tempura' },
  { src: '/images/gallery/gallery-25.jpg', alt: 'Picking up Sushi with chopsticks' },
  { src: '/images/gallery/gallery-26.jpg', alt: 'Kiku Sushi Bar' },

  // Adding menu images
  { src: '/images/menu/swan-lake.jpg', alt: 'Swan Lake - Crispy skin rolled Beijing duck' },
  { src: '/images/menu/gyoza.jpg', alt: 'Gyoza - Pan fried dumplings' },
  { src: '/images/menu/wasabi-shumai.jpg', alt: 'Wasabi Shumai' },
  { src: '/images/menu/crispy-calamari.jpg', alt: 'Crispy Calamari' },
  { src: '/images/menu/chicken-yakitori.jpg', alt: 'Chicken Yakitori' },
  { src: '/images/menu/rock-shrimp.jpg', alt: 'Rock Shrimp' },
  { src: '/images/menu/shrimp-and-vegetable-fried-rice.jpg', alt: 'Shrimp & Vegetable Fried Rice' },
  { src: '/images/menu/grilled-swordfish.jpg', alt: 'Grilled Swordfish' },
  { src: '/images/menu/basil-filet-mignon.jpg', alt: 'Basil Filet Mignon' },
  { src: '/images/menu/chilean-seabass.jpg', alt: 'Chilean Seabass' },
  { src: '/images/menu/grill-king-wild-salmon.jpg', alt: 'Grilled Salmon' },
  { src: '/images/menu/ahi-tuna-steak.jpg', alt: 'Ahi Tuna Steak' },
  { src: '/images/menu/seafood-yaki-soba.jpg', alt: 'Seafood Yaki Udon' },
  { src: '/images/menu/pink-lady.jpg', alt: 'Pink Lady Roll' },
  { src: '/images/menu/rocknroll.jpg', alt: 'Rock \'N Roll' },
  { src: '/images/menu/new-orleans.jpg', alt: 'New Orleans Roll' },
  { src: '/images/menu/crazy-tuna.jpg', alt: 'Crazy Tuna Roll' },
  { src: '/images/menu/bonzai.jpg', alt: 'Bonzai Roll' },
  { src: '/images/menu/sashimi-dinner.jpg', alt: 'Sashimi Dinner' },
];

const assignSpans = () => {
  return galleryImages.map((image, index) => {
    let spanClass;
    if (index % 7 === 0 || index % 7 === 5) {
      spanClass = 'span-3'; // Large
    } else if (index % 7 === 2 || index % 7 === 6) {
      spanClass = 'span-2'; // Medium
    } else {
      spanClass = 'span-1'; // Standard
    }
    return { ...image, spanClass };
  });
};

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [imagesWithSpans] = useState(assignSpans());
  const [imagesLoaded, setImagesLoaded] = useState({});
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setSelectedImage(galleryImages[newIndex].src);
    setCurrentIndex(newIndex);
  }, [currentIndex]);

  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedImage(galleryImages[newIndex].src);
    setCurrentIndex(newIndex);
  }, [currentIndex]);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  }, []);

  const openModal = useCallback((image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'ArrowRight') nextImage(e);
      if (e.key === 'ArrowLeft') prevImage(e);
      if (e.key === 'Escape') closeModal();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, nextImage, prevImage, closeModal]);

  return (
    <section className="gallery-section" id="gallery" ref={ref}>
      <div className="gallery-container">
        <motion.h2
          className="gallery-title"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          GALLERY
        </motion.h2>
        
        <motion.div 
          className="gallery-masonry"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          {imagesWithSpans.map((image, index) => {
            const isHovered = hoveredIndex === index;
            
            return (
              <motion.div 
                className={`gallery-item ${image.spanClass}`}
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView && imagesLoaded[index] ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.05,
                  ease: [0.43, 0.13, 0.23, 0.96]
                }}
                onClick={() => openModal(image.src, index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="image-container">
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    className="gallery-image"
                    loading="lazy"
                    onLoad={() => setImagesLoaded(prev => ({...prev, [index]: true}))}
                  />
                  <motion.div 
                    className="item-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isHovered ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div 
                      className="view-icon"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ 
                        scale: isHovered ? 1 : 0.8, 
                        opacity: isHovered ? 1 : 0 
                      }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                      </svg>
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
      
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedImage} 
                alt={galleryImages[currentIndex].alt} 
                className="modal-image"
              />
              
              <button className="modal-close" onClick={closeModal} aria-label="Close gallery">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                </svg>
              </button>
              
              <button className="modal-nav prev" onClick={prevImage} aria-label="Previous image">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
                </svg>
              </button>
              
              <button className="modal-nav next" onClick={nextImage} aria-label="Next image">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
                </svg>
              </button>
              
              <div className="modal-info">
                <span className="modal-counter">{currentIndex + 1} / {galleryImages.length}</span>
                <span className="modal-caption">{galleryImages[currentIndex].alt}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;